import React, { useState, useEffect } from 'react';
import { getPlatformClass } from '../../utils/platformUtils';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import Fuse from 'fuse.js';
import './SmartProductModal.css';

const SmartProductModal = ({
  isOpen,
  onClose,
  onSave,
  product = null,
  categories = [],
  className = '',
  ...props
}) => {
  const platformClass = getPlatformClass();
  const isEditMode = !!product;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    code: '',
    unit: '',
    thickness: '',
    hasVariants: false,
    // Simple product fields
    currentStock: '',
    minStock: '',
    price: '',
    // Complex product fields
    variants: []
  });

  // Form validation
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Category search functionality
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [showCategoryOptions, setShowCategoryOptions] = useState(false);

  // Variant management
  const [newVariant, setNewVariant] = useState({
    variantName: '',
    size: '',
    color: '',
    thickness: '',
    other: '',
    sku: '',
    currentStock: '',
    minStock: '',
    price: ''
  });
  const [editingVariant, setEditingVariant] = useState(null);
  const [isEditVariantModalOpen, setIsEditVariantModalOpen] = useState(false);

  // Initialize form data when product changes
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        code: product.code || '',
        unit: product.unit || '',
        thickness: product.thickness || '',
        hasVariants: product.hasVariants || false,
        // Simple product fields
        currentStock: product.currentStock || 0,
        minStock: product.minStock || 0,
        price: product.price || 0,
        // Complex product fields
        variants: (product.variants || []).map(variant => ({
          ...variant,
          currentStock: variant.currentStock || 0,
          minStock: variant.minStock || 0,
          price: variant.price || 0
        }))
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category: '',
        code: '',
        unit: '',
        thickness: '',
        hasVariants: false,
        // Simple product fields
        currentStock: '',
        minStock: '',
        price: '',
        // Complex product fields
        variants: []
      });
    }
    setErrors({});
  }, [product]);

  // Fuzzy search for categories
  useEffect(() => {
    if (!formData.category) {
      setFilteredCategories(categories);
      return;
    }
    const fuse = new Fuse(categories, { threshold: 0.3 });
    const results = fuse.search(formData.category).map(r => r.item);
    setFilteredCategories(results);
  }, [formData.category, categories]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle category input change
  const handleCategoryInputChange = (e) => {
    setFormData(prev => ({ ...prev, category: e.target.value }));
    setShowCategoryOptions(true);
    
    // Clear error when user starts typing
    if (errors.category) {
      setErrors(prev => ({
        ...prev,
        category: ''
      }));
    }
  };

  // Handle category option click
  const handleCategoryOptionClick = (category) => {
    setFormData(prev => ({ ...prev, category }));
    setShowCategoryOptions(false);
  };

  // Handle new variant input changes
  const handleNewVariantChange = (field, value) => {
    setNewVariant(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle editing existing variant
  const handleEditVariant = (variant, index) => {
    setEditingVariant({ ...variant, index });
    setIsEditVariantModalOpen(true);
  };

  // Handle variant update
  const handleUpdateVariant = (updatedVariant) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, index) => 
        index === updatedVariant.index ? updatedVariant : variant
      )
    }));
    setEditingVariant(null);
    setIsEditVariantModalOpen(false);
  };

  // Handle variant deletion
  const handleDeleteVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Add a new variant
  const handleAddVariant = () => {
    // All variant fields are now optional - just need at least one field filled
    if (!newVariant.variantName && !newVariant.size && !newVariant.color && !newVariant.thickness && !newVariant.other && !newVariant.sku) {
      setErrors(prev => ({
        ...prev,
        variant: 'At least one variant field must be filled'
      }));
      return;
    }

    // Generate SKU if not provided
    const sku = newVariant.sku || `${formData.name?.substring(0, 3).toUpperCase()}-${(newVariant.size || 'VAR')?.substring(0, 2).toUpperCase()}-${(newVariant.color || 'COL')?.substring(0, 2).toUpperCase()}-${(newVariant.other || 'OTH')?.substring(0, 2).toUpperCase()}`;

    const variant = {
      id: Date.now().toString(), // Temporary ID for UI
      variantName: newVariant.variantName || '',
      size: newVariant.size || '',
      color: newVariant.color || '',
      thickness: newVariant.thickness || '',
      other: newVariant.other || '',
      sku: sku,
      currentStock: parseInt(newVariant.currentStock) || 0,
      minStock: parseInt(newVariant.minStock) || 0,
      price: parseFloat(newVariant.price) || 0
    };

    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, variant]
    }));
    setNewVariant({
      variantName: '',
      size: '',
      color: '',
      thickness: '',
      other: '',
      sku: '',
      currentStock: '',
      minStock: '',
      price: ''
    });
    setErrors(prev => ({ ...prev, variant: '' }));
  };



  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (formData.hasVariants) {
      if (formData.variants.length === 0) {
        newErrors.variants = 'At least one variant is required';
      }
      // Note: Variant fields are not required - they can be empty
    } else {
      if (!formData.minStock || formData.minStock < 0) {
        newErrors.minStock = 'Minimum stock is required and cannot be negative';
      }
      if (!formData.currentStock || formData.currentStock < 0) {
        newErrors.currentStock = 'Initial stock is required and cannot be negative';
      }
      if (!formData.price || formData.price < 0) {
        newErrors.price = 'Price is required and cannot be negative';
      }
    }

    if (formData.code && formData.code.length > 50) {
      newErrors.code = 'Product code is too long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const productData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category.trim(),
        code: formData.code.trim(),
        unit: formData.unit.trim(),
        thickness: formData.thickness.trim(),
        hasVariants: formData.hasVariants,
        updatedAt: new Date().toISOString()
      };

      // Clean up data based on product type
      if (formData.hasVariants) {
        // Complex product - remove simple product fields and process variants
        delete productData.currentStock;
        delete productData.minStock;
        delete productData.price;
        
        // Ensure all variant fields have proper values
        productData.variants = formData.variants.map(variant => ({
          ...variant,
          currentStock: parseInt(variant.currentStock) || 0,
          minStock: parseInt(variant.minStock) || 0,
          price: parseFloat(variant.price) || 0
        }));
      } else {
        // Simple product - remove complex product fields
        delete productData.variants;
        productData.currentStock = parseInt(formData.currentStock) || 0;
        productData.minStock = parseInt(formData.minStock) || 0;
        productData.price = parseFloat(formData.price) || 0;
      }

      if (isEditMode) {
        productData.id = product.id;
        productData.createdAt = product.createdAt;
      } else {
        productData.createdAt = new Date().toISOString();
      }

      await onSave(productData);
      handleClose();
    } catch (error) {
      setErrors({ submit: 'Failed to save product. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      code: '',
      unit: '',
      thickness: '',
      hasVariants: false,
      currentStock: '',
      minStock: '',
      price: '',
      variants: []
    });
    setNewVariant({
      size: '',
      color: '',
      thickness: '',
      other: '',
      sku: '',
      currentStock: '',
      minStock: '',
      price: ''
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`smart-product-modal-overlay ${platformClass}`} onClick={handleBackdropClick}>
      <div className={`smart-product-modal-container ${platformClass} ${className}`} {...props}>
        {/* Modal Header */}
        <div className="smart-product-modal-header">
          <PlatformText variant="h3" className="modal-title">
            {isEditMode ? 'Edit Product' : 'Add New Product'}
          </PlatformText>
          <button
            className="modal-close-button"
            onClick={handleClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="smart-product-modal-content">
          {/* Product Name */}
          <div className="form-group">
            <label htmlFor="product-name" className="form-label">
              <PlatformText variant="body" className="label-text">
                Product Name *
              </PlatformText>
            </label>
            <input
              id="product-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="Enter product name"
              required
            />
            {errors.name && (
              <PlatformText variant="caption" className="error-text">
                {errors.name}
              </PlatformText>
            )}
          </div>

          {/* Product Description */}
          <div className="form-group">
            <label htmlFor="product-description" className="form-label">
              <PlatformText variant="body" className="label-text">
                Description
              </PlatformText>
            </label>
            <textarea
              id="product-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="form-textarea"
              placeholder="Enter product description"
              rows={3}
            />
          </div>

          {/* Category and Code Row */}
          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="product-category" className="form-label">
                <PlatformText variant="body" className="label-text">
                  Category
                </PlatformText>
              </label>
              <input
                id="product-category"
                type="text"
                value={formData.category}
                onChange={handleCategoryInputChange}
                className="form-input"
                placeholder="Enter or search category"
                autoComplete="off"
                onFocus={() => setShowCategoryOptions(true)}
                onBlur={() => setTimeout(() => setShowCategoryOptions(false), 150)}
                style={{ zIndex: 2, position: 'relative' }}
              />
              {showCategoryOptions && (filteredCategories.length > 0 || formData.category.trim()) && (
                <ul className="autocomplete-options">
                  {filteredCategories.map(category => (
                    <li
                      key={category}
                      onMouseDown={() => handleCategoryOptionClick(category)}
                    >
                      {category}
                    </li>
                  ))}
                  {!filteredCategories.includes(formData.category) && formData.category.trim() && (
                    <li className="add-new-option">
                      Add new: <strong>{formData.category}</strong>
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="product-code" className="form-label">
                <PlatformText variant="body" className="label-text">
                  Product Code
                </PlatformText>
              </label>
              <input
                id="product-code"
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                className={`form-input ${errors.code ? 'error' : ''}`}
                placeholder="Enter product code"
                maxLength={50}
              />
              {errors.code && (
                <PlatformText variant="caption" className="error-text">
                  {errors.code}
                </PlatformText>
              )}
            </div>
          </div>

          {/* Unit and Thickness Row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="product-unit" className="form-label">
                <PlatformText variant="body" className="label-text">
                  Unit
                </PlatformText>
              </label>
              <input
                id="product-unit"
                type="text"
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                className="form-input"
                placeholder="e.g., pieces, kg, liters"
              />
            </div>

            <div className="form-group">
              <label htmlFor="product-thickness" className="form-label">
                <PlatformText variant="body" className="label-text">
                  Thickness
                </PlatformText>
              </label>
              <input
                id="product-thickness"
                type="text"
                value={formData.thickness}
                onChange={(e) => handleInputChange('thickness', e.target.value)}
                className="form-input"
                placeholder="e.g., 2mm, 5mm, thin"
              />
            </div>
          </div>

          {/* Product Type Selection */}
          <div className="form-group">
            <label className="form-label">
              <PlatformText variant="body" className="label-text">
                Product Type
              </PlatformText>
            </label>
            <div className="product-type-selection">
              <label className="radio-option">
                <input
                  type="radio"
                  name="hasVariants"
                  checked={!formData.hasVariants}
                  onChange={() => handleInputChange('hasVariants', false)}
                />
                <span className="radio-label">Simple Product (No Variants)</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="hasVariants"
                  checked={formData.hasVariants}
                  onChange={() => handleInputChange('hasVariants', true)}
                />
                <span className="radio-label">Product with Variants</span>
              </label>
            </div>
          </div>

          {/* Simple Product Fields */}
          {!formData.hasVariants && (
            <div className="simple-product-fields">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="product-price" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Price *
                    </PlatformText>
                  </label>
                  <input
                    id="product-price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    className={`form-input ${errors.price ? 'error' : ''}`}
                    placeholder="Enter price"
                    min="0"
                    required
                  />
                  {errors.price && (
                    <PlatformText variant="caption" className="error-text">
                      {errors.price}
                    </PlatformText>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="product-min-stock" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Minimum Stock *
                    </PlatformText>
                  </label>
                  <input
                    id="product-min-stock"
                    type="number"
                    value={formData.minStock}
                    onChange={(e) => handleInputChange('minStock', e.target.value)}
                    className={`form-input ${errors.minStock ? 'error' : ''}`}
                    placeholder="Enter minimum stock level"
                    min="0"
                    required
                  />
                  {errors.minStock && (
                    <PlatformText variant="caption" className="error-text">
                      {errors.minStock}
                    </PlatformText>
                  )}
                </div>
              </div>

              {/* Initial Stock (only for new products) */}
              {!isEditMode && (
                <div className="form-group">
                  <label htmlFor="product-current-stock" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Initial Stock *
                    </PlatformText>
                  </label>
                  <input
                    id="product-current-stock"
                    type="number"
                    value={formData.currentStock}
                    onChange={(e) => handleInputChange('currentStock', e.target.value)}
                    className={`form-input ${errors.currentStock ? 'error' : ''}`}
                    placeholder="Enter initial stock quantity"
                    min="0"
                    required
                  />
                  {errors.currentStock && (
                    <PlatformText variant="caption" className="error-text">
                      {errors.currentStock}
                    </PlatformText>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Complex Product Fields */}
          {formData.hasVariants && (
            <div className="complex-product-fields">
              {/* Variants Section */}
              <div className="variants-section">
                <PlatformText variant="h4" className="variants-title">
                  Product Variants
                </PlatformText>
                
                {/* Add New Variant */}
                <div className="add-variant-form">
                  <div className="form-group">
                    <label htmlFor="variant-name" className="form-label">
                      <PlatformText variant="body" className="label-text">
                        Variant Name
                      </PlatformText>
                    </label>
                    <input
                      id="variant-name"
                      type="text"
                      value={newVariant.variantName}
                      onChange={(e) => handleNewVariantChange('variantName', e.target.value)}
                      className="form-input"
                      placeholder="e.g., Premium Blue Large, Standard Red Medium"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="variant-size" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Size
                        </PlatformText>
                      </label>
                      <input
                        id="variant-size"
                        type="text"
                        value={newVariant.size}
                        onChange={(e) => handleNewVariantChange('size', e.target.value)}
                        className="form-input"
                        placeholder="e.g., Small, Medium, Large"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="variant-color" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Color
                        </PlatformText>
                      </label>
                      <input
                        id="variant-color"
                        type="text"
                        value={newVariant.color}
                        onChange={(e) => handleNewVariantChange('color', e.target.value)}
                        className="form-input"
                        placeholder="e.g., Blue, Red, Green"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="variant-thickness" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Thickness
                        </PlatformText>
                      </label>
                      <input
                        id="variant-thickness"
                        type="text"
                        value={newVariant.thickness}
                        onChange={(e) => handleNewVariantChange('thickness', e.target.value)}
                        className="form-input"
                        placeholder="e.g., 2mm, 5mm, thin"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="variant-other" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Other
                        </PlatformText>
                      </label>
                      <input
                        id="variant-other"
                        type="text"
                        value={newVariant.other}
                        onChange={(e) => handleNewVariantChange('other', e.target.value)}
                        className="form-input"
                        placeholder="e.g., Material, Finish, Style"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="variant-sku" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          SKU
                        </PlatformText>
                      </label>
                      <input
                        id="variant-sku"
                        type="text"
                        value={newVariant.sku}
                        onChange={(e) => handleNewVariantChange('sku', e.target.value)}
                        className="form-input"
                        placeholder="Auto-generated if empty"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="variant-min-stock" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Minimum Stock
                        </PlatformText>
                      </label>
                      <input
                        id="variant-min-stock"
                        type="number"
                        value={newVariant.minStock}
                        onChange={(e) => handleNewVariantChange('minStock', e.target.value)}
                        className="form-input"
                        placeholder="Enter minimum stock level"
                        min="0"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="variant-price" className="form-label">
                        <PlatformText variant="body" className="label-text">
                          Price
                        </PlatformText>
                      </label>
                      <input
                        id="variant-price"
                        type="number"
                        step="0.01"
                        value={newVariant.price}
                        onChange={(e) => handleNewVariantChange('price', e.target.value)}
                        className="form-input"
                        placeholder="Enter price"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="variant-current-stock" className="form-label">
                      <PlatformText variant="body" className="label-text">
                        Initial Stock
                      </PlatformText>
                    </label>
                    <input
                      id="variant-current-stock"
                      type="number"
                      value={newVariant.currentStock}
                      onChange={(e) => handleNewVariantChange('currentStock', e.target.value)}
                      className="form-input"
                      placeholder="Enter initial stock quantity"
                      min="0"
                    />
                  </div>

                  <PlatformButton
                    type="button"
                    onClick={handleAddVariant}
                    className="add-variant-button"
                  >
                    Add Variant
                  </PlatformButton>
                </div>

                {/* Variants List */}
                {formData.variants.length > 0 && (
                  <div className="variants-list">
                    <PlatformText variant="body" className="variants-subtitle">
                      Added Variants ({formData.variants.length})
                    </PlatformText>
                    {formData.variants.map((variant, index) => (
                      <div key={variant.id} className="variant-item">
                        <div 
                          className="variant-info clickable"
                          onClick={() => handleEditVariant(variant, index)}
                          title="Click to edit variant"
                        >
                          <PlatformText variant="body" className="variant-name">
                            {variant.variantName || [
                              variant.size,
                              variant.color,
                              variant.thickness,
                              variant.other
                            ].filter(Boolean).join(' - ')}
                          </PlatformText>
                          <PlatformText variant="caption" className="variant-details">
                            SKU: {variant.sku} | Stock: {variant.currentStock} | Price: ₹{variant.price}
                          </PlatformText>
                        </div>
                        <div className="variant-actions">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditVariant(variant, index);
                            }}
                            className="edit-variant-btn"
                            title="Edit variant"
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVariant(index);
                            }}
                            className="delete-variant-btn"
                            title="Delete variant"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {errors.variants && (
                  <PlatformText variant="caption" className="error-text">
                    {errors.variants}
                  </PlatformText>
                )}
              </div>
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="form-error">
              <PlatformText variant="caption" className="error-text">
                {errors.submit}
              </PlatformText>
            </div>
          )}

          {/* Modal Actions */}
          <div className="smart-product-modal-actions">
            <PlatformButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </PlatformButton>
            
            <PlatformButton
              type="submit"
              disabled={isSubmitting}
              className="save-button"
            >
              {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Product' : 'Add Product')}
            </PlatformButton>
          </div>
        </form>
      </div>

      {/* Edit Variant Modal */}
      {isEditVariantModalOpen && editingVariant && (
        <div className="edit-variant-modal-overlay" onClick={() => setIsEditVariantModalOpen(false)}>
          <div className="edit-variant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-variant-modal-header">
              <PlatformText variant="h4" className="edit-variant-modal-title">
                Edit Variant
              </PlatformText>
              <button
                className="edit-variant-modal-close"
                onClick={() => setIsEditVariantModalOpen(false)}
                aria-label="Close edit variant modal"
              >
                ✕
              </button>
            </div>
            
            <div className="edit-variant-modal-content">
              <div className="form-group">
                <label htmlFor="edit-variant-name" className="form-label">
                  <PlatformText variant="body" className="label-text">
                    Variant Name
                  </PlatformText>
                </label>
                <input
                  id="edit-variant-name"
                  type="text"
                  value={editingVariant.variantName || ''}
                  onChange={(e) => setEditingVariant(prev => ({ ...prev, variantName: e.target.value }))}
                  className="form-input"
                  placeholder="e.g., Premium Blue Large, Standard Red Medium"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-variant-size" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Size
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-size"
                    type="text"
                    value={editingVariant.size}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, size: e.target.value }))}
                    className="form-input"
                    placeholder="e.g., Small, Medium, Large"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-variant-color" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Color
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-color"
                    type="text"
                    value={editingVariant.color}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, color: e.target.value }))}
                    className="form-input"
                    placeholder="e.g., Blue, Red, Green"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-variant-thickness" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Thickness
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-thickness"
                    type="text"
                    value={editingVariant.thickness}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, thickness: e.target.value }))}
                    className="form-input"
                    placeholder="e.g., 2mm, 5mm, thin"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-variant-other" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Other
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-other"
                    type="text"
                    value={editingVariant.other}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, other: e.target.value }))}
                    className="form-input"
                    placeholder="e.g., Material, Finish, Style"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-variant-sku" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      SKU
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-sku"
                    type="text"
                    value={editingVariant.sku}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, sku: e.target.value }))}
                    className="form-input"
                    placeholder="Enter SKU code"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-variant-min-stock" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Minimum Stock
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-min-stock"
                    type="number"
                    value={editingVariant.minStock}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, minStock: e.target.value }))}
                    className="form-input"
                    placeholder="Enter minimum stock level"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-variant-price" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Price
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-price"
                    type="number"
                    step="0.01"
                    value={editingVariant.price}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, price: e.target.value }))}
                    className="form-input"
                    placeholder="Enter price"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-variant-current-stock" className="form-label">
                    <PlatformText variant="body" className="label-text">
                      Current Stock
                    </PlatformText>
                  </label>
                  <input
                    id="edit-variant-current-stock"
                    type="number"
                    value={editingVariant.currentStock}
                    onChange={(e) => setEditingVariant(prev => ({ ...prev, currentStock: e.target.value }))}
                    className="form-input"
                    placeholder="Enter current stock quantity"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div className="edit-variant-modal-actions">
              <PlatformButton
                type="button"
                variant="outline"
                onClick={() => setIsEditVariantModalOpen(false)}
                className="cancel-button"
              >
                Cancel
              </PlatformButton>
              
              <PlatformButton
                type="button"
                onClick={() => handleUpdateVariant(editingVariant)}
                className="save-button"
              >
                Update Variant
              </PlatformButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartProductModal; 